import { ApolloError } from "@apollo/client";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { useEffect, useMemo, useState } from "react";

import { AddNodeInput, NodeLevel } from "../../../API";
import CodeSnippetBox from "../../../common/components/box/CodeSnippetBox";
import StyledDialog from "../../../common/components/dialog/StyledDialog";
import DeviceTemplateNodeSelect from "../../../common/components/layout/components/DeviceTemplateNodeSelect";
import SKUSelect from "../../../common/components/layout/components/SKUSelect";
import LocationSelect from "../../../common/components/select/LocationSelect/LocationSelect";
import confirmDialogStyleOptions from "../../../common/helpers/confirmDialogParams";
import { useCustomerExternalIdGuard } from "../../../common/hooks/useCustomerExternalIdGuard";
import { useCustomerIdGuard } from "../../../common/hooks/useCustomerIdGuard";
import { useGetCustomer } from "../../../common/hooks/useGetCustomer";
import { AutocompleteOptionType } from "../../../common/models/autocomplete";
import { skuCatalog } from "../../../common/models/enums";
import { checkDuplicateDeviceNames } from "../../../common/utils/checkDuplicateDeviceNames";
import { resolveProductCount } from "../../../common/utils/generateDeviceName";
import {
  generateNodeName,
  resolveNodeNameConflicts,
} from "../../../common/utils/generateNodeName";
import { mapTemplateDataToDeviceInput } from "../../../common/utils/mapTemplateDataToDeviceInput";
import { mapTemplateDataToServiceInput } from "../../../common/utils/mapTemplateDataToServiceInput";
import { validateCreateDevicesFromTemplate } from "../../../common/utils/validateCreateDevicesFromTemplate";
import { DEFAULT_DEVICE_PASSWORD } from "../../../common/variables/common";
import {
  errorNotification,
  successNotification,
} from "../../../common/variables/notification";
import { useSelectedLocationDropdown } from "../../../common/variables/selectedLocationDropdown";
import { useGetDevicesByLocation } from "../../customer-settings/tabs/locations/hooks/useGetDevicesByLocation";
import { useGetLocationsCached } from "../../customer-settings/tabs/locations/hooks/useGetLocationsCached";
import { useSyncShadow } from "../../model-manager/hooks/useSyncShadow";
import { useCreateNode } from "../../nodes/hooks/useCreateNode";
import { useGetNodes } from "../../nodes/hooks/useGetNodes";
import { useUpdateNode } from "../../nodes/hooks/useUpdateNode";
import { ISKUDeviceData } from "../../system-settings/tabs/skus/variables/skuData";
import { useAddProduct } from "../hooks/useAddProduct";
import { useCreateBatchDevices } from "../hooks/useCreateBatchDevices";
import { useCreateBatchServices } from "../hooks/useCreateBatchServices";

type OnboardingComponent = {
  name: string;
  type: string;
  makeModelId: string;
  hasPT: boolean;
  models: string[];
  hasRecordVideo?: boolean;
  password?: string;
  defaultNodeName?: string;
};

type NodeRow = {
  location: AutocompleteOptionType;
  nodeName: string;
  nodeIP: string;
  componentSerialNumber: string;
};

type CreatedNodeInfo = {
  nodeId: string;
  nodeName: string;
  nodeIP: string;
  onboardCommand: string;
};

type NodeAssignment = {
  nodeId: string;
  nodeIP: string;
  nodeName: string;
  location?: string;
  serialNumber?: string;
};

type ParentSystemOption = {
  serialNumber: string;
  configurationName?: string | null;
  skuNumber: string;
  nodeIds: string[];
};

interface SelectedSKU {
  id: string;
  skuNumber: string;
  configurationName: string;
  category: string;
  skuDataConfigurations: string;
  nodeCount: number;
}

interface CreateDevicesFromSKUDialogProps {
  open: boolean;
  handleClose: () => void;
}

const IPV4_REGEX =
  /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

const CreateDevicesFromSKUDialog = ({
  open,
  handleClose,
}: CreateDevicesFromSKUDialogProps) => {
  // const theme = useTheme();
  const confirm = useConfirm();
  const selectedCustomerId = useCustomerIdGuard();
  const customerExternalId = useCustomerExternalIdGuard();
  const selectedLocation = useSelectedLocationDropdown();

  // Location-specific nodes - used for collision detection only
  const { loading: locationNodesLoading, refetch: refetchLocationNodes } =
    useGetNodes({
      locationId: selectedLocation?.value,
      includeDevices: false,
      fetchPolicy: "network-only",
    });
  const { data: currentDeviceData, refetchDevices } = useGetDevicesByLocation(
    selectedLocation?.value ?? ""
  );
  const { customer } = useGetCustomer();
  const { syncShadowsForNode } = useSyncShadow();
  const { batchCreateDevices, loading: devicesLoading } =
    useCreateBatchDevices();
  const { batchCreateServices } = useCreateBatchServices();
  const { createNode } = useCreateNode();
  const { updateNode } = useUpdateNode();
  const { addProduct } = useAddProduct();
  const { locations } = useGetLocationsCached();

  // Step navigation
  const [activeStep, setActiveStep] = useState(0);

  // Step 0 state
  const [selectedSKU, setSelectedSKU] = useState<SelectedSKU | null>(null);
  const [serialNumber, setSerialNumber] = useState("");
  const [devicePassword, setDevicePassword] = useState("");
  const [step0Submitted, setStep0Submitted] = useState(false);

  // Step 1 state (node setup)
  const [nodeRows, setNodeRows] = useState<NodeRow[]>([]);
  const [createdNodes, setCreatedNodes] = useState<CreatedNodeInfo[]>([]);
  const [nodesCreated, setNodesCreated] = useState(false);
  const [nodeCreationLoading, setNodeCreationLoading] = useState(false);
  const [productCount, setProductCount] = useState<number>(1);

  // Step 2 state (devices)
  const [components, setComponents] = useState<OnboardingComponent[]>([]);
  const [deviceIpAddresses, setDeviceIpAddresses] = useState<string[]>([]);
  const [selectedNodes, setSelectedNodes] = useState<NodeAssignment[]>([]);
  const [step2Submitted, setStep2Submitted] = useState(false);

  // Step 1 accessory state
  const [selectedParentProduct, setSelectedParentProduct] =
    useState<ParentSystemOption | null>(null);
  const [selectedAccessoryNode, setSelectedAccessoryNode] =
    useState<NodeAssignment>({
      nodeId: "",
      nodeIP: "",
      nodeName: "",
    });
  const [step1AccessorySubmitted, setStep1AccessorySubmitted] = useState(false);
  const [useStandaloneAccessory, setUseStandaloneAccessory] = useState(false);
  const [nodeQueryLocation, setNodeQueryLocation] =
    useState<AutocompleteOptionType | null>(null);

  const nodeCount = selectedSKU?.nodeCount ?? 0;
  const isLinkAccessory = (selectedSKU?.nodeCount ?? 0) === 0;
  const steps =
    nodeCount >= 1
      ? ["Template", "Node Setup", "Devices & Submit"]
      : ["Template", "Node Selection", "Devices & Submit"];
  const deviceStepIndex = nodeCount >= 1 || isLinkAccessory ? 2 : 1;
  // Location-scoped nodes — defaults to sidebar location; user can override in Step 1
  const { data: allNodesData, loading: allNodesLoading } = useGetNodes({
    locationId: nodeQueryLocation?.value,
    includeDevices: true,
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (!devicePassword) {
      setDevicePassword(customer?.devicePassword || DEFAULT_DEVICE_PASSWORD);
    }
  }, [customer?.devicePassword]);

  // Auto-create nodes when entering Node Setup step — no manual input needed
  useEffect(() => {
    if (
      activeStep === 1 &&
      nodeCount >= 1 &&
      !nodesCreated &&
      !nodeCreationLoading &&
      !locationNodesLoading // Ensure location nodes are loaded for collision detection
    ) {
      handleCreateNodes();
    }
  }, [activeStep, locationNodesLoading]); // Also re-run when location nodes finish loading

  const allCustomerNodes = useMemo(() => {
    if (!allNodesData) return [];

    return (
      allNodesData.getNodes?.items
        ?.map(node => {
          const rawLocationId =
            node?.locationId?.replace("C_", "C#").replace("_L_", "#L#") ?? "";
          const locationName =
            locations?.find(l => l?.id === rawLocationId)?.name ?? "";

          return {
            nodeId:
              node?.nodeId
                ?.replace("C_", "C#")
                .replace("_L_", "#L#")
                .replace("_N_", "#N#") ?? "",
            nodeIP: node?.nodeIP ?? "",
            nodeName: node?.nodeName ?? "",
            location: locationName,
            serialNumber: node?.serialNumber ?? "",
          };
        })
        .filter(n => n.nodeId && n.nodeName)
        .sort((a, b) => a.location.localeCompare(b.location)) ?? []
    );
  }, [allNodesData, locations]);

  const existingNames =
    currentDeviceData?.getDevicesByLocation?.items
      ?.map(d => d?.id?.replace(/^DE#/, "").toLowerCase())
      .filter((id): id is string => !!id) ?? [];

  const resetDialogState = () => {
    setActiveStep(0);

    setSelectedSKU(null);

    setSerialNumber("");

    setDevicePassword("");

    setStep0Submitted(false);

    setNodeRows([]);

    setCreatedNodes([]);

    setNodesCreated(false);

    setNodeCreationLoading(false);

    setProductCount(1);

    setComponents([]);

    setDeviceIpAddresses([]);

    setSelectedNodes([]);

    setStep2Submitted(false);

    setSelectedParentProduct(null);

    setSelectedAccessoryNode({ nodeId: "", nodeIP: "", nodeName: "" });

    setStep1AccessorySubmitted(false);

    setUseStandaloneAccessory(false);

    setNodeQueryLocation(null);
  };

  const closeDialog = (): void => {
    handleClose();

    resetDialogState();
  };

  const confirmDialogClose = (): void => {
    confirm({
      title: "Are you sure you want to cancel?",
      confirmationText: "Cancel",
      cancellationText: "Back",
      ...confirmDialogStyleOptions,
      confirmationButtonProps: {
        ...confirmDialogStyleOptions.confirmationButtonProps,
        color: "primary",
      },
    })
      .then(async (): Promise<void> => {
        closeDialog();
      })
      .catch(() => {
        // User clicked "Back" — keep open
      });
  };

  const handleSKUChange = ({
    id,
    skuNumber,
    configurationName,
    category,
    skuDataConfigurations,
    nodeCount: newNodeCount,
  }: {
    id: string;
    skuNumber: string;
    configurationName: string;
    category: string;
    skuDataConfigurations: string;
    nodeCount: number;
  }) => {
    try {
      const catalogEntry = skuCatalog[skuNumber];

      if (!catalogEntry) {
        errorNotification(`Unknown SKU number: ${skuNumber}`);

        return;
      }

      const configData: ISKUDeviceData[] = JSON.parse(skuDataConfigurations);

      // Filter out AGX (nodes) — POE and LWOP are cameras and shown as device rows
      const merged: OnboardingComponent[] = catalogEntry.subComponents
        .filter(sub => sub.type !== "AGX")
        .map(sub => {
          const configEntry = configData.find(c => c.name === sub.name);

          return {
            name: sub.name,
            type: sub.type,
            makeModelId: sub.makeModelId,
            hasPT: sub.hasPT,
            models: configEntry?.models ?? [],
            hasRecordVideo: configEntry?.hasRecordVideo,
            password: configEntry?.password,
            defaultNodeName: configEntry?.defaultNodeName,
          };
        });

      setSelectedSKU({
        id,
        skuNumber,
        configurationName,
        category,
        skuDataConfigurations,
        nodeCount: newNodeCount,
      });

      setComponents(merged);

      const rawDeviceIps = merged.map(comp => {
        const configEntry = configData.find(c => c.name === comp.name);

        return (configEntry?.ipAddress ?? "").replaceAll(" ", "");
      });

      // CC_MULT components share one physical IP — propagate the first found value
      // to all positions so Step 2 is pre-filled without requiring a manual onChange
      const firstMultIp =
        rawDeviceIps.find(
          (ip, i) => merged[i]?.makeModelId === "CC_MULT" && ip
        ) ?? "";

      const deviceIps = rawDeviceIps.map((ip, i) =>
        merged[i]?.makeModelId === "CC_MULT" && firstMultIp ? firstMultIp : ip
      );

      setDeviceIpAddresses(deviceIps);

      // Pre-fill password from SKU config if set, otherwise keep customer default
      const skuPassword = configData[0]?.password;

      if (skuPassword) {
        setDevicePassword(skuPassword);
      }

      setSelectedNodes(
        Array(merged.length).fill({ nodeId: "", nodeIP: "", nodeName: "" })
      );

      // Initialize node rows for Step 1 (collision resolution happens in handleCreateNodes)
      const initRows: NodeRow[] = Array.from(
        { length: newNodeCount },
        (_, i) => ({
          location: selectedLocation,
          nodeName: generateNodeName(selectedLocation?.title ?? "", i),
          nodeIP: "",
          componentSerialNumber: "",
        })
      );

      setNodeRows(initRows);

      setCreatedNodes([]);

      setNodesCreated(false);

      setStep0Submitted(false);

      setStep2Submitted(false);

      refetchDevices(selectedLocation?.value ?? "");
    } catch {
      errorNotification("Invalid system template data format");
    }
  };

  // Step 0: advance to node setup or devices
  const handleStep0Next = () => {
    setStep0Submitted(true);

    if (!selectedSKU) {
      errorNotification("Please select a system template");

      return;
    }

    if (!serialNumber) {
      errorNotification("Please enter a serial number");

      return;
    }

    if (devicePassword.length > 0 && devicePassword.length < 8) {
      errorNotification("Password must be at least 8 characters");

      return;
    }

    setActiveStep(nodeCount >= 1 || isLinkAccessory ? 1 : deviceStepIndex);
  };

  // Step 1: create all nodes sequentially
  const handleCreateNodes = async () => {
    const missing = nodeRows.some(r => !r.location?.value);

    if (missing) {
      errorNotification("All nodes require a location");

      return;
    }

    setNodeCreationLoading(true);
    const created: CreatedNodeInfo[] = [];

    try {
      // Refetch location-specific nodes for collision detection
      const refetchResult = await refetchLocationNodes();

      // Build set of existing node names at this location
      const existingNodeNames = new Set(
        refetchResult.data?.getNodes?.items
          ?.map(n => n?.nodeName ?? "")
          .filter(Boolean) ?? []
      );

      // Resolve product count by checking both existing devices and nodes
      const acronym = selectedSKU
        ? skuCatalog[selectedSKU.skuNumber]?.acronym ?? ""
        : "";
      const allExistingNames = [
        ...existingNames,
        ...Array.from(existingNodeNames),
      ];
      const resolvedProductCount = resolveProductCount(
        acronym,
        allExistingNames
      );

      setProductCount(resolvedProductCount);

      // Group nodes by location and assign per-location indices
      const locationCounts: Record<string, number> = {};
      const updatedNodeRows = nodeRows.map(row => {
        const locationKey = row.location?.title ?? "";

        locationCounts[locationKey] = (locationCounts[locationKey] || 0) + 1;
        const locationIndex = locationCounts[locationKey];
        const rawName = generateNodeName(
          locationKey,
          locationIndex - 1,
          acronym,
          resolvedProductCount
        );

        return { ...row, nodeName: rawName };
      });

      // Resolve all names at once to handle collisions
      const rawNames = updatedNodeRows.map(row => row.nodeName);
      const resolvedNames = resolveNodeNameConflicts(
        rawNames,
        existingNodeNames
      );

      // Update nodeRows with resolved names
      const finalNodeRows = updatedNodeRows.map((row, i) => ({
        ...row,
        nodeName: resolvedNames[i],
      }));

      for (let i = 0; i < finalNodeRows.length; i++) {
        const row = finalNodeRows[i];
        const input: AddNodeInput = {
          customerExternalId,
          locationId: row.location!.value,
          nodeName: row.nodeName,
          nodeIP: row.nodeIP,
          level: NodeLevel.NODE,
          tags: [],
        };

        try {
          const result = await createNode(input);

          if (result?.data?.addNode) {
            const node = result.data.addNode;
            const rawNodeId = node.nodeId ?? "";

            created.push({
              nodeId: rawNodeId
                .replace("C_", "C#")
                .replace("_L_", "#L#")
                .replace("_N_", "#N#"),
              nodeName: node.nodeName ?? "",
              nodeIP: node.nodeIP ?? "",
              onboardCommand: node.onboardCommand ?? "",
            });
          }
        } catch (nodeError: unknown) {
          const gqlErrors =
            nodeError instanceof Error && "graphQLErrors" in nodeError
              ? (nodeError as { graphQLErrors: { errorType?: string }[] })
                  .graphQLErrors
              : [];
          const isDuplicate = gqlErrors.some(
            e => e?.errorType === "NODE_ALREADY_EXISTS"
          );

          if (isDuplicate) {
            errorNotification(
              `Node '${row.nodeName}' already exists at this location. Please rename and try again.`
            );
            break;
          }

          throw nodeError;
        }
      }

      if (created.length === finalNodeRows.length) {
        // Update nodeRows with the resolved names for consistency
        setNodeRows(finalNodeRows);

        setCreatedNodes(created);

        setNodesCreated(true);
      }
    } catch {
      errorNotification("Failed to create nodes");
    } finally {
      setNodeCreationLoading(false);
    }
  };

  // Step 1: accessory — select parent system and node then advance
  const handleStep1AccessoryNext = () => {
    setStep1AccessorySubmitted(true);

    if (!useStandaloneAccessory && !selectedParentProduct) return;

    if (!selectedAccessoryNode.nodeId) return;

    setSelectedNodes(Array(components.length).fill(selectedAccessoryNode));

    setActiveStep(deviceStepIndex);
  };

  // Step 1: continue to devices (update nodes with sku/serial, advance step)
  const handleContinueToDevices = async () => {
    for (let i = 0; i < createdNodes.length; i++) {
      const node = createdNodes[i];

      await updateNode({
        nodeId: node.nodeId
          .replaceAll("C_", "C#")
          .replaceAll("_L_", "#L#")
          .replaceAll("_N_", "#N#"),
        skuNumber: selectedSKU?.skuNumber ?? undefined,
        serialNumber: serialNumber || undefined,
        componentSerialNumber: nodeRows[i]?.componentSerialNumber || undefined,
      });
    }

    // Pre-fill node assignment using SKU config defaults when available
    const defaultNode = createdNodes[0]
      ? {
          nodeId: createdNodes[0].nodeId,
          nodeIP: createdNodes[0].nodeIP,
          nodeName: createdNodes[0].nodeName,
        }
      : { nodeId: "", nodeIP: "", nodeName: "" };

    const catalogEntry = selectedSKU ? skuCatalog[selectedSKU.skuNumber] : null;
    const agxNodes =
      catalogEntry?.subComponents.filter(s => s.type === "AGX") ?? [];

    const nodeAssignments = components.map(comp => {
      if (comp.defaultNodeName && createdNodes.length > 0) {
        const agxIndex = agxNodes.findIndex(
          a => a.name === comp.defaultNodeName
        );

        if (agxIndex >= 0 && createdNodes[agxIndex]) {
          return {
            nodeId: createdNodes[agxIndex].nodeId,
            nodeIP: createdNodes[agxIndex].nodeIP,
            nodeName: createdNodes[agxIndex].nodeName,
          };
        }
      }

      return defaultNode;
    });

    setSelectedNodes(nodeAssignments);

    setActiveStep(deviceStepIndex);
  };

  // Step 2: node selection pool — if nodes were created in Step 1, limit to those
  const availableNodes: NodeAssignment[] = useMemo(() => {
    if (nodesCreated && createdNodes.length > 0) {
      return createdNodes.map((node, i) => ({
        nodeId: node.nodeId,
        nodeIP: node.nodeIP,
        nodeName: node.nodeName,
        location: nodeRows[i]?.location?.title ?? "",
      }));
    }

    return allCustomerNodes;
  }, [nodesCreated, createdNodes, allCustomerNodes, nodeRows]);

  const eligibleParentSystems: ParentSystemOption[] = useMemo(() => {
    const items = currentDeviceData?.getDevicesByLocation?.items ?? [];
    const grouped = new Map<string, ParentSystemOption>();

    for (const device of items) {
      if (
        !device?.serialNumber ||
        !(
          device.skuNumber?.startsWith("001") ||
          device.skuNumber?.startsWith("002")
        )
      )
        continue;

      const existing = grouped.get(device.serialNumber);

      if (existing) {
        if (device.nodeId && !existing.nodeIds.includes(device.nodeId)) {
          existing.nodeIds.push(device.nodeId);
        }
      } else {
        grouped.set(device.serialNumber, {
          serialNumber: device.serialNumber,
          skuNumber: device.skuNumber ?? "",
          nodeIds: device.nodeId ? [device.nodeId] : [],
        });
      }
    }

    return Array.from(grouped.values());
  }, [currentDeviceData]);

  const accessoryEligibleNodes = useMemo(() => {
    // Wait for all node pages to load before filtering
    if (allNodesLoading) return [];

    if (useStandaloneAccessory) return allCustomerNodes;

    if (!selectedParentProduct?.serialNumber) return [];

    // Filter by serialNumber (primary) with nodeIds fallback for historical data
    const parentNodeIds = new Set(
      (selectedParentProduct.nodeIds ?? []).filter(Boolean) as string[]
    );

    return allCustomerNodes.filter(
      n =>
        n.serialNumber === selectedParentProduct.serialNumber ||
        parentNodeIds.has(n.nodeId)
    );
  }, [
    allNodesLoading,
    useStandaloneAccessory,
    selectedParentProduct,
    allCustomerNodes,
  ]);

  const handleDeviceIpChange = (index: number, value: string) => {
    const cleaned = value.replaceAll(" ", "");

    setDeviceIpAddresses(prev => {
      const updated = [...prev];

      if (components[index]?.makeModelId === "CC_MULT") {
        // Propagate shared IP to all CC_MULT components
        components.forEach((comp, i) => {
          if (comp.makeModelId === "CC_MULT") {
            updated[i] = cleaned;
          }
        });
      } else {
        updated[index] = cleaned;
      }

      return updated;
    });
  };

  const handleNodeSelect = (index: number, node: NodeAssignment) => {
    setSelectedNodes(prev => {
      const updated = [...prev];

      updated[index] = node;

      return updated;
    });
  };

  // Step 2: submit devices + services + product unit
  const saveDevices = async (): Promise<void> => {
    setStep2Submitted(true);

    if (!selectedSKU || components.length === 0) {
      errorNotification("Please select a system template");

      return;
    }

    // For CC_MULT groups, only the first entry needs to be validated (shared IP)
    const firstMultIndex = components.findIndex(
      c => c.makeModelId === "CC_MULT"
    );
    const missingIps = deviceIpAddresses.some(
      (ip, i) =>
        !ip.trim() &&
        (components[i]?.makeModelId !== "CC_MULT" || i === firstMultIndex)
    );

    if (missingIps) {
      errorNotification("All device IP addresses are required");

      return;
    }

    const invalidIps = deviceIpAddresses.some((ip, i) => {
      if (!ip.trim()) return false;

      if (components[i]?.makeModelId === "CC_MULT" && i !== firstMultIndex)
        return false;

      return !IPV4_REGEX.test(ip.trim());
    });

    if (invalidIps) {
      errorNotification("One or more device IP addresses have invalid format");

      return;
    }

    const missingNodes = selectedNodes.some(n => !n.nodeId);

    if (missingNodes) {
      errorNotification("All devices must be assigned to a node");

      return;
    }

    // Build template data — for hasPT components, auto-derive PT IP = device IP (CH4-UCv2)
    const templateData = components.map((comp, index) => ({
      name: comp.name,
      makeModelId: comp.makeModelId,
      deviceIp: deviceIpAddresses[index] || "",
      models: comp.models,
      isPT: comp.hasPT,
      panTiltIP: comp.hasPT ? deviceIpAddresses[index] : undefined,
      panTiltType: comp.hasPT ? "CH4-UCv2" : undefined,
      hasRecordVideo: comp.hasRecordVideo ?? true,
      password: comp.password,
    }));

    const validation = validateCreateDevicesFromTemplate({
      parsedTemplateData: templateData,
      selectedNodes,
      devicePassword,
    });

    if (!validation.isValid) {
      errorNotification(validation.errorMessage!);

      return;
    }

    try {
      // Resolve productCount for Link accessories (nodeCount === 0)
      // Products with nodes resolve this in handleCreateNodes
      let finalProductCount = productCount;

      if (nodeCount === 0) {
        const acronym = skuCatalog[selectedSKU.skuNumber]?.acronym ?? "";

        finalProductCount = resolveProductCount(acronym, existingNames);

        setProductCount(finalProductCount);
      }

      const devicesToCreate = mapTemplateDataToDeviceInput({
        templateData,
        selectedCustomerId,
        selectedLocation: selectedLocation ?? { title: "", value: "" },
        selectedNodes,
        devicePassword,
        skuNumber: selectedSKU.skuNumber,
        serialNumber: serialNumber || undefined,
        productCount: finalProductCount,
        parentProductSerialNumber:
          selectedParentProduct?.serialNumber ?? undefined,
      });

      const uniqueDevices = checkDuplicateDeviceNames(
        existingNames,
        devicesToCreate
      );

      const servicesToCreate = mapTemplateDataToServiceInput(
        uniqueDevices,
        templateData,
        serialNumber || undefined
      );

      await batchCreateDevices(uniqueDevices);

      if (servicesToCreate.length > 0) {
        const serviceResponse = await batchCreateServices(servicesToCreate);

        if (serviceResponse?.errors) {
          errorNotification("Failed to create services");

          return;
        }
      }

      // Record product unit — include all created nodes plus any existing nodes (Link accessories)
      const uniqueNodeIds = [
        ...Array.from(
          new Set(
            [
              ...createdNodes.map(n => n.nodeId),
              ...selectedNodes.map(n => n.nodeId),
            ].filter(Boolean)
          )
        ),
      ];
      try {
        await addProduct({
          skuNumber: selectedSKU.skuNumber,
          serialNumber: serialNumber || "",
          locationId: selectedLocation?.value ?? "",
          customerId: selectedCustomerId,
          nodeIds: uniqueNodeIds,
          parentSerialNumber: selectedParentProduct?.serialNumber ?? undefined,
        });
      } catch {
        console.error("Failed to record product");
      }

      closeDialog();

      successNotification("Product onboarded successfully!");

      try {
        await Promise.all(
          uniqueNodeIds.map(nodeId => syncShadowsForNode(nodeId))
        );
      } catch (error) {
        console.error(error);
      }
    } catch (error) {
      if (
        (error as ApolloError).message.includes(
          "The conditional request failed"
        )
      ) {
        errorNotification("Device ID already exists");
      } else {
        errorNotification((error as ApolloError).message);
      }
    }
  };

  return (
    <StyledDialog
      open={open}
      title="Onboard a Product"
      onClose={confirmDialogClose}
      showSubmitButton={false}
      maxWidth="xl"
      sx={{
        "& .MuiDialog-paper": { width: "95vw", maxWidth: "95vw" },
        overflow: "visible",
      }}
    >
      {/* Stepper header */}
      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {steps.map(label => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* ── Step 0: Configuration ── */}
      {activeStep === 0 && (
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Location
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {selectedLocation?.title ?? "—"}
            </Typography>
          </Grid>

          <Grid item>
            <SKUSelect
              skuId={selectedSKU?.id ?? ""}
              onSKUChange={handleSKUChange}
              error={step0Submitted && !selectedSKU?.skuNumber}
              helperText={
                step0Submitted && !selectedSKU?.skuNumber
                  ? "SKU selection is required"
                  : ""
              }
            />
          </Grid>

          <Grid item>
            <TextField
              fullWidth
              label="Unit Serial Number (S/N)"
              value={serialNumber}
              onChange={e => setSerialNumber(e.target.value)}
              error={step0Submitted && !serialNumber}
              helperText={
                step0Submitted && !serialNumber
                  ? "Serial number is required"
                  : "Serial number for this physical unit"
              }
            />
          </Grid>

          <Grid item>
            <TextField
              fullWidth
              label="Device Password"
              value={devicePassword}
              onChange={e => setDevicePassword(e.target.value)}
              placeholder="Camera credentials (optional)"
              error={devicePassword.length > 0 && devicePassword.length < 8}
              helperText={
                devicePassword.length > 0 && devicePassword.length < 8
                  ? "Password must be at least 8 characters"
                  : "This will be the default credential used when onboarding devices. Can be overridden per onboarding."
              }
            />
          </Grid>
        </Grid>
      )}

      {/* ── Step 1: Node Setup ── */}
      {activeStep === 1 && nodeCount >= 1 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {nodeCreationLoading && (
            <Typography variant="body2" color="text.secondary">
              Creating nodes and getting onboarding commands...
            </Typography>
          )}

          {nodeRows.map((row, index) => (
            <Box
              key={index}
              sx={{
                pb: 2,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Node {index + 1}
              </Typography>

              {/* Onboard command — shown after node is created */}
              {nodesCreated && createdNodes[index]?.onboardCommand && (
                <Box sx={{ mt: 2, "& > div": { width: "100% !important" } }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5, display: "block" }}
                  >
                    Run this script on {createdNodes[index].nodeName}:
                  </Typography>
                  <CodeSnippetBox
                    nodeScript={createdNodes[index].onboardCommand}
                    notify={() =>
                      successNotification("Script copied to clipboard!")
                    }
                  />
                </Box>
              )}
            </Box>
          ))}

          {/* Warning banner shown after nodes are created */}
          {nodesCreated && (
            <Alert severity="warning" sx={{ width: "fit-content" }}>
              Run the onboarding script on each node before continuing.
            </Alert>
          )}
        </Box>
      )}

      {/* ── Step 1: Node Selection (Link accessories only) ── */}
      {activeStep === 1 && isLinkAccessory && (
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Alert severity="info" sx={{ mb: 1 }}>
              This is a Link accessory. Choose whether to attach it to an
              existing system or deploy it standalone.
            </Alert>
          </Grid>

          <Grid item>
            <RadioGroup
              row
              value={useStandaloneAccessory ? "standalone" : "attached"}
              onChange={e => {
                const standalone = e.target.value === "standalone";

                setUseStandaloneAccessory(standalone);

                setSelectedParentProduct(null);

                setSelectedAccessoryNode({
                  nodeId: "",
                  nodeIP: "",
                  nodeName: "",
                });

                if (!standalone) setNodeQueryLocation(null);
              }}
            >
              <FormControlLabel
                value="attached"
                control={<Radio />}
                label="Attach to existing system"
              />
              <FormControlLabel
                value="standalone"
                control={<Radio />}
                label="Standalone — select any node"
              />
            </RadioGroup>
          </Grid>

          {useStandaloneAccessory && (
            <Grid item>
              <LocationSelect
                value={nodeQueryLocation ?? selectedLocation ?? null}
                onChange={val => {
                  setNodeQueryLocation(val?.value ? val : null);

                  setSelectedAccessoryNode({
                    nodeId: "",
                    nodeIP: "",
                    nodeName: "",
                  });
                }}
                size="small"
                includeAllLocations={false}
              />
            </Grid>
          )}

          {!useStandaloneAccessory && (
            <Grid item>
              {eligibleParentSystems.length === 0 ? (
                <Alert severity="warning">
                  No compatible parent systems found at this location. Onboard a
                  Minerva or Halo system first.
                </Alert>
              ) : (
                <Autocomplete
                  fullWidth
                  options={eligibleParentSystems}
                  getOptionLabel={p =>
                    `${p.configurationName ?? p.skuNumber} — ${p.serialNumber}`
                  }
                  value={selectedParentProduct}
                  onChange={(_, val) => {
                    setSelectedParentProduct(val);

                    setSelectedAccessoryNode({
                      nodeId: "",
                      nodeIP: "",
                      nodeName: "",
                    });
                  }}
                  isOptionEqualToValue={(opt, val) =>
                    opt.serialNumber === val.serialNumber
                  }
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Parent System"
                      error={step1AccessorySubmitted && !selectedParentProduct}
                      helperText={
                        step1AccessorySubmitted && !selectedParentProduct
                          ? "Parent system is required"
                          : "Select the Minerva or Halo this Link is attached to"
                      }
                    />
                  )}
                />
              )}
            </Grid>
          )}

          <Grid item>
            <DeviceTemplateNodeSelect
              selectedNodeId={selectedAccessoryNode.nodeId}
              filteredNodes={accessoryEligibleNodes}
              onChange={node => setSelectedAccessoryNode(node)}
              error={step1AccessorySubmitted && !selectedAccessoryNode.nodeId}
              helperText={
                step1AccessorySubmitted && !selectedAccessoryNode.nodeId
                  ? "Node is required"
                  : useStandaloneAccessory
                  ? "Select the processing node for this Link"
                  : !selectedParentProduct
                  ? "Select a parent system first"
                  : ""
              }
              disabled={!useStandaloneAccessory && !selectedParentProduct}
              size="small"
              fullWidth
            />
          </Grid>
        </Grid>
      )}

      {/* ── Step 2: Devices & Submit ── */}
      {activeStep === deviceStepIndex && (
        <Grid container direction="column" spacing={2}>
          {components.length === 0 && (
            <Grid item>
              <Typography color="text.secondary">
                No device components found for this SKU.
              </Typography>
            </Grid>
          )}

          {(() => {
            const firstMultIndex = components.findIndex(
              c => c.makeModelId === "CC_MULT"
            );

            return components
              .map((comp, originalIndex) => ({ comp, originalIndex }))
              .sort((a, b) => a.comp.name.localeCompare(b.comp.name))
              .map(({ comp, originalIndex: index }) => (
                <Grid item key={comp.name + index}>
                  <Box
                    sx={{
                      mb: 1,
                      pb: 2,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    {/* Component header */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {comp.name}
                      <Chip
                        label={comp.type}
                        size="small"
                        sx={{ ml: 1, fontWeight: 600 }}
                      />
                      {comp.models.length > 0 && (
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 1 }}
                        >
                          {comp.models
                            .map(m => {
                              switch (m) {
                                case "gasleak":
                                  return "leak";
                                case "tlm":
                                  return "tanklevel";
                                default:
                                  return m;
                              }
                            })
                            .join(", ")}
                        </Typography>
                      )}
                    </Typography>
                    <Divider
                      sx={{
                        mb: 1.5,
                        mt: 0.5,
                        borderBottomWidth: 2,
                        borderColor: "primary.main",
                      }}
                    />

                    <Grid container spacing={1}>
                      {/* Device IP — CC_MULT shares one IP; only show input on first occurrence */}
                      {comp.makeModelId === "CC_MULT" &&
                      index !== firstMultIndex ? (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            IP: {deviceIpAddresses[firstMultIndex] || "—"}{" "}
                            (shared multisensor)
                          </Typography>
                        </Grid>
                      ) : (
                        <Grid item xs={12} sm={6}>
                          <TextField
                            size="small"
                            fullWidth
                            label={
                              comp.makeModelId === "CC_MULT"
                                ? "Multisensor IP (shared)"
                                : "Device IP address"
                            }
                            value={deviceIpAddresses[index] || ""}
                            onChange={e =>
                              handleDeviceIpChange(index, e.target.value)
                            }
                            placeholder="192.168.1.x"
                            error={
                              step2Submitted &&
                              (!deviceIpAddresses[index]?.trim() ||
                                !IPV4_REGEX.test(
                                  deviceIpAddresses[index]?.trim()
                                ))
                            }
                            helperText={
                              step2Submitted &&
                              !deviceIpAddresses[index]?.trim()
                                ? "IP address is required"
                                : step2Submitted &&
                                  !IPV4_REGEX.test(
                                    deviceIpAddresses[index]?.trim()
                                  )
                                ? "Invalid IP address format"
                                : comp.makeModelId === "CC_MULT"
                                ? "Used for all multisensor channels"
                                : comp.hasPT
                                ? "PT IP auto-set to device IP (CH4-UCv2)"
                                : ""
                            }
                          />
                        </Grid>
                      )}

                      {/* Node assignment */}
                      <Grid item xs={12} sm={6}>
                        <DeviceTemplateNodeSelect
                          selectedNodeId={selectedNodes[index]?.nodeId ?? ""}
                          filteredNodes={availableNodes}
                          onChange={node => handleNodeSelect(index, node)}
                          error={
                            step2Submitted && !selectedNodes[index]?.nodeId
                          }
                          helperText={
                            step2Submitted && !selectedNodes[index]?.nodeId
                              ? "Node is required"
                              : isLinkAccessory
                              ? "Node assigned from previous step"
                              : ""
                          }
                          disabled={isLinkAccessory}
                          size="small"
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              ));
          })()}
        </Grid>
      )}

      {/* ── Navigation buttons ── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          mt: 3,
          pt: 2,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        {/* Back button */}
        {activeStep > 0 && (
          <Button
            variant="outlined"
            onClick={() => {
              if (
                activeStep === deviceStepIndex &&
                (nodeCount >= 1 || isLinkAccessory)
              ) {
                setActiveStep(1);
              } else {
                setActiveStep(0);
              }
            }}
          >
            Back
          </Button>
        )}

        {/* Step 0: Next */}
        {activeStep === 0 && (
          <Button variant="contained" onClick={handleStep0Next}>
            Next
          </Button>
        )}

        {/* Step 1: Accessory Next */}
        {activeStep === 1 && isLinkAccessory && (
          <Button variant="contained" onClick={handleStep1AccessoryNext}>
            Next
          </Button>
        )}

        {/* Step 1: Continue after nodes are created */}
        {activeStep === 1 && nodeCount >= 1 && nodesCreated && (
          <Button variant="contained" onClick={handleContinueToDevices}>
            Continue to Device Setup
          </Button>
        )}

        {/* Step 2: Create Devices */}
        {activeStep === deviceStepIndex && (
          <LoadingButton
            variant="contained"
            loading={devicesLoading}
            onClick={saveDevices}
          >
            Create Devices
          </LoadingButton>
        )}
      </Box>
    </StyledDialog>
  );
};

export default CreateDevicesFromSKUDialog;
